const PROMO_KINDS = ['badge', 'price'];
const PROMO_COLOR_TONES = ['orange', 'beige', 'secondary', 'black'];
const PROMO_COLOR_TONE_ALIASES = {
  sand: 'beige',
  emerald: 'secondary',
  blue: 'black',
};

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, Number(value)));
}

function roundCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return NaN;
  return Number(numeric.toFixed(2));
}

function calculatePromoDiscountedPrice(basePrice, discountPercent) {
  const safeBasePrice = Number(basePrice);
  const safeDiscountPercent = Number(discountPercent);
  if (!Number.isFinite(safeBasePrice) || !Number.isFinite(safeDiscountPercent)) return NaN;
  return roundCurrency(Math.max(0, safeBasePrice * (1 - clampNumber(safeDiscountPercent, 0, 100) / 100)));
}

function normalizePromoDiscountPercent(value) {
  const numeric = toOptionalNumber(value);
  if (numeric === null) return null;
  return roundCurrency(clampNumber(numeric, 0, 100));
}

function buildEmptyPromo(overrides = {}) {
  return {
    enabled: false,
    kind: 'badge',
    labelI18n: { en: '', fr: '' },
    titleI18n: { en: '', fr: '' },
    descriptionI18n: { en: '', fr: '' },
    discountPercent: null,
    startsAt: null,
    endsAt: null,
    featured: false,
    colorTone: 'orange',
    ...overrides,
  };
}

function normalizePromoKind(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return PROMO_KINDS.includes(normalized) ? normalized : 'badge';
}

function normalizePromoColorTone(value) {
  const normalized = String(value || '').trim().toLowerCase();
  const aliased = PROMO_COLOR_TONE_ALIASES[normalized] || normalized;
  return PROMO_COLOR_TONES.includes(aliased) ? aliased : 'orange';
}

function normalizePromoTextMap(value) {
  return {
    en: String(value?.en || '').trim(),
    fr: String(value?.fr || '').trim(),
  };
}

function toOptionalNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizePromoDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizePromo(value = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return buildEmptyPromo();
  }

  const kind = normalizePromoKind(value.kind);
  const discountPercent = kind === 'price' ? normalizePromoDiscountPercent(value.discountPercent) : null;

  return buildEmptyPromo({
    enabled: Boolean(value.enabled),
    kind,
    labelI18n: normalizePromoTextMap(value.labelI18n),
    titleI18n: normalizePromoTextMap(value.titleI18n),
    descriptionI18n: normalizePromoTextMap(value.descriptionI18n),
    discountPercent,
    startsAt: normalizePromoDate(value.startsAt),
    endsAt: normalizePromoDate(value.endsAt),
    featured: Boolean(value.featured),
    colorTone: normalizePromoColorTone(value.colorTone),
  });
}

function validatePromo(promo, { basePrice } = {}) {
  const normalizedPromo = normalizePromo(promo);
  if (!normalizedPromo.enabled) {
    return { ok: true, promo: normalizedPromo };
  }

  const startsAt = normalizedPromo.startsAt ? new Date(normalizedPromo.startsAt) : null;
  const endsAt = normalizedPromo.endsAt ? new Date(normalizedPromo.endsAt) : null;

  if (startsAt && endsAt && startsAt.getTime() > endsAt.getTime()) {
    return { ok: false, error: 'Promotion end date must be after the start date', promo: normalizedPromo };
  }

  if (normalizedPromo.kind === 'price') {
    const safeBasePrice = Number(basePrice);
    if (!Number.isFinite(safeBasePrice) || safeBasePrice <= 0) {
      return { ok: false, error: 'Price promotions require a valid base price', promo: normalizedPromo };
    }
    if (Number.isFinite(normalizedPromo.discountPercent)) {
      if (normalizedPromo.discountPercent < 0 || normalizedPromo.discountPercent > 100) {
        return { ok: false, error: 'Discount percentage must be between 0 and 100', promo: normalizedPromo };
      }
    } else {
      return { ok: false, error: 'Discount percentage must be a valid number', promo: normalizedPromo };
    }
  }

  return { ok: true, promo: normalizedPromo };
}

function resolvePromoText(map = {}, language = 'en') {
  return String(map?.[language] || map?.en || map?.fr || '').trim();
}

function localizePromo(promo, language = 'en') {
  const normalizedPromo = normalizePromo(promo);
  return {
    ...normalizedPromo,
    label: resolvePromoText(normalizedPromo.labelI18n, language),
    title: resolvePromoText(normalizedPromo.titleI18n, language),
    description: resolvePromoText(normalizedPromo.descriptionI18n, language),
  };
}

function isPromoActive(promo, now = new Date()) {
  const normalizedPromo = normalizePromo(promo);
  if (!normalizedPromo.enabled) return false;

  const nowDate = now instanceof Date ? now : new Date(now);
  const startsAt = normalizedPromo.startsAt ? new Date(normalizedPromo.startsAt) : null;
  const endsAt = normalizedPromo.endsAt ? new Date(normalizedPromo.endsAt) : null;

  if (startsAt && startsAt.getTime() > nowDate.getTime()) return false;
  if (endsAt && endsAt.getTime() < nowDate.getTime()) return false;
  return true;
}

function getActivePromo(item, language = 'en', now = new Date()) {
  const promo = localizePromo(item?.promo, language);
  return isPromoActive(promo, now) ? promo : null;
}

function getEffectivePrice(item, language = 'en', now = new Date()) {
  const basePrice = Number(item?.price ?? 0);
  const activePromo = getActivePromo(item, language, now);
  if (activePromo?.kind === 'price') {
    if (Number.isFinite(activePromo.discountPercent)) {
      const discountedPrice = calculatePromoDiscountedPrice(basePrice, activePromo.discountPercent);
      if (Number.isFinite(discountedPrice)) {
        return discountedPrice;
      }
    }
  }
  return basePrice;
}

function getPromoDiscountPercent(promo, basePrice) {
  const normalizedPromo = normalizePromo(promo);
  if (normalizedPromo.kind !== 'price') return null;
  if (Number.isFinite(normalizedPromo.discountPercent)) {
    return normalizedPromo.discountPercent;
  }
  return null;
}

function getPromoSortScore(item, now = new Date()) {
  const promo = normalizePromo(item?.promo);
  if (!isPromoActive(promo, now)) return 0;
  return promo.featured ? 2 : 1;
}

function sortItemsByPromo(items = [], now = new Date()) {
  return [...(Array.isArray(items) ? items : [])].sort((left, right) => {
    const scoreDelta = getPromoSortScore(right, now) - getPromoSortScore(left, now);
    if (scoreDelta !== 0) return scoreDelta;
    return 0;
  });
}

function getPromoToneClasses(colorTone = 'orange') {
  switch (normalizePromoColorTone(colorTone)) {
    case 'beige':
      return {
        badge: 'border-[var(--dash-other)]/70 bg-[var(--dash-other)] !text-[var(--dash-black)]',
        callout: 'border-[var(--dash-other)]/35 bg-[var(--dash-other)]/15 text-[var(--dash-black)]',
      };
    case 'secondary':
      return {
        badge: 'border-[rgba(var(--dash-secondary-rgb),0.7)] bg-[rgba(var(--dash-secondary-rgb),1)] text-white',
        callout: 'border-[rgba(var(--dash-secondary-rgb),0.35)] bg-[rgba(var(--dash-secondary-rgb),0.14)] text-white',
      };
    case 'black':
      return {
        badge: 'border-[var(--dash-black)]/70 bg-[var(--dash-black)] text-white',
        callout: 'border-[var(--dash-black)]/35 bg-[var(--dash-black)]/10 text-white',
      };
    case 'orange':
    default:
      return {
        badge: 'border-[var(--dash-main)]/70 bg-[var(--dash-main)] text-[var(--dash-black)]',
        callout: 'border-[var(--dash-main)]/35 bg-[var(--dash-main)]/10 text-[var(--dash-main)]',
      };
  }
}

function getPromoFlashColor(colorTone = 'orange') {
  switch (normalizePromoColorTone(colorTone)) {
    case 'beige':
      return 'rgba(228, 188, 155, 0.72)';
    case 'secondary':
      return 'var(--dash-secondary)';
    case 'black':
      return 'rgba(255, 255, 255, 0.38)';
    case 'orange':
    default:
      return 'rgba(255, 93, 16, 0.72)';
  }
}

function toDateTimeLocalValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function normalizePromoFormValue(value = {}, { basePrice } = {}) {
  const normalizedPromo = normalizePromo(value);
  const discountPercent = normalizedPromo.kind === 'price' ? normalizedPromo.discountPercent : '';
  return {
    ...normalizedPromo,
    discountPercent:
      discountPercent === null || discountPercent === undefined ? '' : Number(discountPercent),
    startsAt: toDateTimeLocalValue(normalizedPromo.startsAt),
    endsAt: toDateTimeLocalValue(normalizedPromo.endsAt),
  };
}

module.exports = {
  PROMO_COLOR_TONES,
  PROMO_KINDS,
  calculatePromoDiscountedPrice,
  buildEmptyPromo,
  getActivePromo,
  getEffectivePrice,
  getPromoDiscountPercent,
  getPromoFlashColor,
  getPromoSortScore,
  getPromoToneClasses,
  isPromoActive,
  localizePromo,
  normalizePromo,
  normalizePromoFormValue,
  sortItemsByPromo,
  toDateTimeLocalValue,
  validatePromo,
};
